import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircleQuestion, X, Star, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';

const BETA_MODE = process.env.REACT_APP_BETA_MODE === 'true';
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STRINGS = {
  en: {
    trigger: "How's the beta going?",
    title: 'Share your beta feedback',
    subtitle: 'Bugs, ideas, first impressions — anything helps us ship a better product.',
    placeholder: 'What worked, what broke, what would you love to see next?',
    ratingLabel: 'Overall (optional)',
    submit: 'Send feedback',
    thanks: 'Thanks! Your feedback is with the team.',
    error: 'Could not send — please try again.',
    empty: 'Please write a few words before sending.',
  },
  bg: {
    trigger: 'Как върви бетата?',
    title: 'Сподели твоя бета фидбек',
    subtitle: 'Бъгове, идеи, първи впечатления — всичко ни помага да направим по-добър продукт.',
    placeholder: 'Какво сработи, какво се счупи, какво би искал да видиш следващо?',
    ratingLabel: 'Обща оценка (по избор)',
    submit: 'Изпрати',
    thanks: 'Благодарим! Твоят фидбек стигна до екипа.',
    error: 'Неуспешно изпращане — опитай отново.',
    empty: 'Моля напиши няколко думи преди изпращане.',
  },
  es: {
    trigger: '¿Cómo va la beta?',
    title: 'Comparte tu feedback de la beta',
    subtitle: 'Bugs, ideas, primeras impresiones — todo nos ayuda a mejorar el producto.',
    placeholder: '¿Qué funcionó, qué falló, qué te gustaría ver a continuación?',
    ratingLabel: 'Valoración (opcional)',
    submit: 'Enviar',
    thanks: '¡Gracias! Tu feedback ya está con el equipo.',
    error: 'No se pudo enviar — inténtalo de nuevo.',
    empty: 'Escribe unas palabras antes de enviar.',
  },
  de: {
    trigger: 'Wie läuft die Beta?',
    title: 'Teile dein Beta-Feedback',
    subtitle: 'Bugs, Ideen, erste Eindrücke — alles hilft uns, ein besseres Produkt zu bauen.',
    placeholder: 'Was hat funktioniert, was ist kaputt, was möchtest du als Nächstes sehen?',
    ratingLabel: 'Gesamtbewertung (optional)',
    submit: 'Senden',
    thanks: 'Danke! Dein Feedback ist beim Team angekommen.',
    error: 'Konnte nicht gesendet werden — bitte erneut versuchen.',
    empty: 'Bitte schreibe ein paar Worte vor dem Senden.',
  },
};

export default function BetaFeedbackWidget() {
  const { language } = useLanguage();
  const location = useLocation();
  const t = STRINGS[language] || STRINGS.en;
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  if (!BETA_MODE) return null;
  // Hide on static asset routes used for marketing screenshots
  if (location.pathname === '/end-card') return null;

  const submit = async () => {
    const msg = message.trim();
    if (!msg) {
      toast.error(t.empty);
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND}/api/beta-feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('access_token')
            ? { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
            : {}),
        },
        body: JSON.stringify({ message: msg, rating: rating || null }),
      });
      if (!res.ok) throw new Error(await res.text());
      toast.success(t.thanks);
      setMessage('');
      setRating(0);
      setOpen(false);
    } catch (err) {
      console.error('beta-feedback failed', err);
      toast.error(t.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Floating trigger */}
      <motion.button
        type="button"
        onClick={() => setOpen(true)}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.2 }}
        whileHover={{ scale: 1.03 }}
        whileTap={{ scale: 0.97 }}
        className="fixed bottom-6 right-6 z-40 flex items-center gap-2 rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/25 px-5 py-3 text-sm font-medium hover:shadow-xl transition-shadow"
        data-testid="beta-feedback-trigger"
        aria-label={t.trigger}
      >
        <MessageCircleQuestion className="w-4 h-4" />
        <span className="hidden sm:inline">{t.trigger}</span>
      </motion.button>

      {/* Modal */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
              onClick={() => setOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.96 }}
              transition={{ type: 'spring', damping: 24, stiffness: 240 }}
              className="fixed z-50 inset-x-4 bottom-4 sm:inset-auto sm:bottom-24 sm:right-6 sm:w-[420px] rounded-2xl border border-border bg-card p-6 shadow-2xl"
              role="dialog"
              aria-modal="true"
              data-testid="beta-feedback-modal"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-heading text-xl font-semibold">{t.title}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{t.subtitle}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="p-1 -m-1 rounded-full hover:bg-muted transition-colors"
                  aria-label="Close"
                  data-testid="beta-feedback-close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={t.placeholder}
                rows={4}
                maxLength={2000}
                data-testid="beta-feedback-textarea"
                className="w-full resize-none rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/40 transition-shadow"
              />

              <div className="mt-4 flex items-center justify-between">
                <div>
                  <div className="text-xs text-muted-foreground mb-1.5">{t.ratingLabel}</div>
                  <div className="flex items-center gap-1" data-testid="beta-feedback-rating">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button
                        key={n}
                        type="button"
                        onMouseEnter={() => setHover(n)}
                        onMouseLeave={() => setHover(0)}
                        onClick={() => setRating(rating === n ? 0 : n)}
                        className="p-1 -m-1"
                        aria-label={`Rate ${n} of 5`}
                        data-testid={`beta-feedback-star-${n}`}
                      >
                        <Star
                          className={`w-5 h-5 transition-colors ${
                            n <= (hover || rating)
                              ? 'fill-primary text-primary'
                              : 'text-muted-foreground/40'
                          }`}
                        />
                      </button>
                    ))}
                  </div>
                </div>

                <Button
                  onClick={submit}
                  disabled={submitting}
                  className="rounded-full"
                  data-testid="beta-feedback-submit"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    t.submit
                  )}
                </Button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
